import { type CSSProperties } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { BarRole } from "../../types/auth";

interface Props {
  barRoles: BarRole[];
  onOpenFullMenu: () => void;
}

export default function AdminBottomNav({ onOpenFullMenu }: Props) {
  const location = useLocation();
  const path = location.pathname;

  const isShelfMapActive = path.startsWith("/admin/shelf-map");
  const isCashActive = path.startsWith("/admin/cash-management");

  const isServiceActive =
    !isShelfMapActive &&
    !isCashActive &&
    (path.startsWith("/admin/operations") ||
      path.startsWith("/admin/pick-list") ||
      path.startsWith("/admin/recipes") ||
      path.startsWith("/admin/prep-recipes") ||
      path.startsWith("/admin/ingredients") ||
      path.startsWith("/admin/cocktail-mapping") ||
      path.startsWith("/admin/tasting-mat") ||
      path.startsWith("/admin/flights"));

  const isInventoryActive =
    path.startsWith("/admin/inventory") ||
    path.startsWith("/admin/inventory-count") ||
    path.startsWith("/admin/whiskey-intel") ||
    path.startsWith("/admin/enrichment") ||
    path.startsWith("/admin/catalog-monitor") ||
    path.startsWith("/admin/merge") ||
    path.startsWith("/admin/curation");

  const isGuestsActive =
    path.startsWith("/admin/members") ||
    path.startsWith("/admin/crm") ||
    path.startsWith("/admin/club-pours") ||
    path.startsWith("/admin/events") ||
    path.startsWith("/admin/communications") ||
    path.startsWith("/admin/reviews") ||
    path.startsWith("/admin/trails");

  const navTabs = [
    { to: "/admin/operations", label: "Service", icon: "🍸", isActive: isServiceActive },
    { to: "/admin/shelf-map", label: "Shelf Map", icon: "🗺️", isActive: isShelfMapActive },
    { to: "/admin/cash-management", label: "Cash", icon: "💵", isActive: isCashActive },
    { to: "/admin/inventory", label: "Inventory", icon: "📦", isActive: isInventoryActive },
    { to: "/admin/members", label: "Guests", icon: "👥", isActive: isGuestsActive },
  ];

  return (
    <nav style={styles.bottomBar} aria-label="Mobile Bottom Navigation">
      <div style={styles.tabGrid}>
        {navTabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            style={styles.navItem}
          >
            <div
              style={{
                ...styles.pillContainer,
                ...(tab.isActive ? styles.pillContainerActive : styles.pillContainerInactive),
              }}
            >
              <span
                style={{
                  ...styles.navIcon,
                  ...(tab.isActive ? styles.navIconActive : {}),
                }}
              >
                {tab.icon}
              </span>
              <span
                style={{
                  ...styles.navLabel,
                  color: tab.isActive ? "var(--gold, #F9AF36)" : "rgba(255, 255, 255, 0.55)",
                  fontWeight: tab.isActive ? 800 : 600,
                }}
              >
                {tab.label}
              </span>
            </div>
          </NavLink>
        ))}

        <button
          type="button"
          onClick={onOpenFullMenu}
          style={{
            ...styles.navItem,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
          aria-label="Open Full Menu Directory"
        >
          <div style={{ ...styles.pillContainer, ...styles.pillContainerInactive }}>
            <span style={styles.navIcon}>☰</span>
            <span style={{ ...styles.navLabel, color: "rgba(255, 255, 255, 0.55)", fontWeight: 600 }}>
              Menu
            </span>
          </div>
        </button>
      </div>
    </nav>
  );
}

const styles: Record<string, CSSProperties> = {
  bottomBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: "calc(56px + env(safe-area-inset-bottom, 0px))",
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
    background: "rgba(14, 11, 8, 0.92)",
    backdropFilter: "blur(24px) saturate(180%)",
    WebkitBackdropFilter: "blur(24px) saturate(180%)",
    borderTop: "1px solid rgba(249, 175, 54, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 990,
    boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.65)",
    paddingLeft: 4,
    paddingRight: 4,
  },
  tabGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: 640,
    height: 56,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textDecoration: "none",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    padding: "2px",
  },
  pillContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: 58,
    height: 44,
    padding: "3px 2px",
    borderRadius: 10,
    transition: "all 0.18s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  pillContainerActive: {
    background: "rgba(249, 175, 54, 0.16)",
    border: "1px solid rgba(249, 175, 54, 0.32)",
    boxShadow: "0 0 12px rgba(249, 175, 54, 0.12)",
  },
  pillContainerInactive: {
    background: "transparent",
    border: "1px solid transparent",
  },
  navIcon: {
    fontSize: 16,
    lineHeight: 1,
    marginBottom: 3,
    transition: "transform 0.18s ease",
  },
  navIconActive: {
    transform: "scale(1.1)",
    filter: "drop-shadow(0 0 4px rgba(249, 175, 54, 0.4))",
  },
  navLabel: {
    fontSize: 10,
    letterSpacing: 0.15,
    whiteSpace: "nowrap",
    textAlign: "center",
    transition: "color 0.18s ease",
    lineHeight: 1.1,
  },
};
